import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { MultipleDocumentUploader } from '@/common/components/MultipleDocumentUploader'
import SelectController from '@/common/components/SelectController'
import Table from '@/common/components/Table'
import TextArea from '@/common/components/TextArea'
import TextField from '@/common/components/TextField'
import useBlobs from '@/common/hooks/queries/useBlobs'
import useDebounce from '@/common/hooks/useDebounce'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUser from '@/common/hooks/useUser'
import { Mapper } from '@/common/services/mapper'
import { TicketData } from '@/common/types/support/ticket.type'
import { cn } from '@/common/utils/cn'
import { FormTransformers } from '@/common/utils/formTransformers'
import { handlePastedImageUpload } from '@/common/utils/handlePastedImageUpload'
import { useSupportReasons } from '@/features/support/hooks/useSupportReasons'
import { ModalAddSupportTickets } from '@/features/support/internal/components/ModalAddSupportTickets'
import { ModalAddUsers } from '@/features/support/internal/components/ModalAddUsers'
import { supportTicketColumns } from '@/features/support/internal/constants/supportTicketColumns.constant'
import { userColumns } from '@/features/support/internal/constants/userColumns.constant'
import { useCreateInternalSupportTicket } from '@/features/support/internal/hooks/useCreateInternalSupportTicket'
import {
  internalSupportTicketSchema,
  InternalSupportTicketValidationSchema,
} from '@/features/support/internal/schemas/create.schema'
import { InternalSupportTicketsTransformer } from '@/features/support/internal/services/internalSupportTicketsTransformer'
import { SupportTicketAvailableUserWithRelationType } from '@/features/support/internal/types/supportTicketAvailableUser.type'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeftIcon } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

const InternalSupportCreate = () => {
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [users, setUsers] = useState<SupportTicketAvailableUserWithRelationType[]>([])
  const [supportTickets, setSupportTickets] = useState<TicketData[]>([])
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchSupportReason, setSearchSupportReason] = useDebounce('')

  const {
    register,
    handleSubmit,
    control,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<InternalSupportTicketValidationSchema>({
    resolver: zodResolver(internalSupportTicketSchema),
  })

  const { data: supportReasonOptions, isPending: supportReasonsIsPending } = useSupportReasons({
    filters: { search: searchSupportReason },
    mapper: Mapper.mapToOptions({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  })

  const { data: user } = useUser({ id: 'me' })

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs()

  const { mutate: createSupportTicket, isPending: createSupportTicketIsPending } = useCreateInternalSupportTicket({
    creator_id: user?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internalSupportTickets'] })
      goBack({ fallback: '/internal-support' })
    },
    onError: FormTransformers.errorsTransformer<InternalSupportTicketValidationSchema>({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<InternalSupportTicketValidationSchema> = (data) => {
    if (!users?.length || !users.some((user) => user.relationType === 'responsible')) {
      return setMessages(['O chamado deve ter ao menos um usuário responsável.'])
    }

    createSupportTicket({ form: data, users, supportTickets })
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex gap-4 items-center mb-6">
          <Button variant="link-white" onClick={() => goBack({ fallback: '/internal-support' })}>
            <ChevronLeftIcon size={40} strokeWidth={1} />
          </Button>

          <h2 className="text-2xl font-bold">Abertura de chamado interno</h2>
        </div>

        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-128px)]">
          <div className="flex flex-col gap-y-4">
            <div className="flex items-start justify-between">
              <p className="text-lg font-bold text-white-800">Usuários vinculados</p>

              <ModalAddUsers users={users} setUsers={setUsers} />
            </div>

            <Table
              className={cn('max-h-[14.75rem]', {
                'min-h-14': users?.length > 0,
              })}
              columns={userColumns()}
              rows={InternalSupportTicketsTransformer.usersTableTransformer()(users, setUsers) || []}
              showEmptyTable
              rowEmptyValue="--"
              page={1}
              totalPages={1}
              isLoadingData={false}
            />
          </div>

          <SelectController
            control={control}
            name="reason"
            label="Motivo"
            markAsRequired
            errorMessage={errors.reason?.message}
            options={supportReasonOptions || []}
            onInputChange={setSearchSupportReason}
            isDisabled={supportReasonsIsPending || createSupportTicketIsPending}
          />

          <div className="flex flex-col gap-y-4">
            <div className="flex items-start justify-between">
              <p className="text-lg font-bold text-white-800">Chamados vinculados</p>

              <ModalAddSupportTickets supportTickets={supportTickets} setSupportTickets={setSupportTickets} />
            </div>

            <Table
              className={cn('max-h-[14.75rem]', {
                'min-h-14': supportTickets?.length > 0,
              })}
              columns={supportTicketColumns}
              rows={
                InternalSupportTicketsTransformer.supportTicketsTableTransformer()({
                  supportTickets,
                  setSupportTickets,
                  actionType: 'remove',
                }) || []
              }
              showEmptyTable
              rowEmptyValue="--"
              page={1}
              totalPages={1}
              isLoadingData={false}
            />
          </div>

          <TextField
            label="Assunto"
            markAsRequired
            {...register('subject_line')}
            errorMessage={errors.subject_line?.message}
            disabled={createSupportTicketIsPending}
          />

          <TextArea
            rows={16}
            maxLength={2048}
            errorMessage={errors.content?.message}
            onPaste={(event) =>
              handlePastedImageUpload({
                event,
                uploadBlob,
                getValues,
                setValue,
                fieldName: 'documents'
              })
            }
            {...register('content')}
            disabled={createSupportTicketIsPending || uploadBlobIsPending}
          />

          <div>
            <MultipleDocumentUploader
              control={control}
              name="documents"
              uploadBlob={uploadBlob}
              fieldName="documents"
              accept=".png, .jpg, .jpeg, .webp, .pdf, .doc, .docx, .xls, .xlsx"
              isLoading={uploadBlobIsPending}
              setError={(message) => setError?.('documents', { message })}
              disabled={createSupportTicketIsPending || uploadBlobIsPending}
            />
            {errors.documents?.message && <ErrorMessage message={errors.documents?.message} />}
          </div>

          <Button
            name="submit"
            type="submit"
            className="self-end"
            disabled={createSupportTicketIsPending || uploadBlobIsPending}
            isLoading={createSupportTicketIsPending}
          >
            Enviar
          </Button>
        </Box>
      </form>

      <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
    </>
  )
}

export default InternalSupportCreate
